import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Phone, MapPin, CheckCircle2, MessageCircle } from "lucide-react";
import { Reveal, MaskLineInView } from "./Reveal";
import { sendContact, getSettings } from "@/lib/api";

const PAKETE = ["Basic – 49 €", "Business – 79 €", "Premium – 99 €", "Noch unsicher"];

const DEFAULT_CONTACT = {
  email: "saba@saba-business-development.de",
  phone: "+49 157 50186769",
  whatsapp: "+49 157 50186769",
  whatsapp_message: "Hallo Landingsstrategie, ich interessiere mich für eine Website.",
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", paket: "", has_website: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [CONTACT, setContact] = useState(DEFAULT_CONTACT);

  useEffect(() => {
    getSettings()
      .then((s) => setContact({
        email: s?.contact_email || DEFAULT_CONTACT.email,
        phone: s?.contact_phone || DEFAULT_CONTACT.phone,
        whatsapp: s?.whatsapp || DEFAULT_CONTACT.whatsapp,
        whatsapp_message: s?.whatsapp_message ?? DEFAULT_CONTACT.whatsapp_message,
      }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onSelect = (e) => {
      const match = PAKETE.find((p) => p.startsWith(String(e.detail).split(" ")[0])) || e.detail;
      setForm((f) => ({ ...f, paket: match }));
    };
    window.addEventListener("ls:selectPaket", onSelect);
    return () => window.removeEventListener("ls:selectPaket", onSelect);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und eine kurze Nachricht ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      setForm({ name: "", company: "", email: "", phone: "", paket: "", has_website: "", message: "" });
      setSubmitted(true);
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const waDigits = (CONTACT.whatsapp || "").replace(/\D/g, "");
  const waHref = waDigits ? `https://wa.me/${waDigits}${CONTACT.whatsapp_message ? `?text=${encodeURIComponent(CONTACT.whatsapp_message)}` : ""}` : null;

  const field = "w-full bg-transparent border-b border-white/20 py-4 text-white placeholder:text-neutral-600 focus:border-[#FF3B00] focus:outline-none transition-colors duration-300";

  return (
    <section id="kontakt" className="relative py-14 lg:py-20 border-t border-white/10" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14">
        <div>
          <Reveal className="font-tech text-xs uppercase tracking-[0.25em] text-[#FF3B00] mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#FF3B00]" /> Unverbindliche Anfrage
          </Reveal>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-white uppercase mb-8">
            <MaskLineInView>Starten Sie Ihren</MaskLineInView>
            <MaskLineInView delay={0.1} className="text-[#FF3B00]">Website-Service.</MaskLineInView>
          </h2>
          <Reveal delay={0.2} className="text-neutral-400 text-base leading-relaxed max-w-md mb-10">
            Erzählen Sie uns kurz von Ihrem Unternehmen. Wir melden uns unverbindlich mit den nächsten
            Schritten – ganz ohne Verpflichtung.
          </Reveal>

          <Reveal delay={0.3} className="space-y-5">
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-white group" data-testid="contact-email">
              <span className="inline-flex h-11 w-11 items-center justify-center border border-white/10 group-hover:border-[#FF3B00] transition-colors">
                <Mail size={18} className="text-[#FF3B00]" />
              </span>
              <span className="link-underline break-all">{CONTACT.email}</span>
            </a>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 text-white group" data-testid="contact-phone">
              <span className="inline-flex h-11 w-11 items-center justify-center border border-white/10 group-hover:border-[#FF3B00] transition-colors">
                <Phone size={18} className="text-[#FF3B00]" />
              </span>
              <span className="link-underline">{CONTACT.phone}</span>
            </a>
            <div className="flex items-center gap-4 text-neutral-400" data-testid="contact-address">
              <span className="inline-flex h-11 w-11 items-center justify-center border border-white/10">
                <MapPin size={18} className="text-[#FF3B00]" />
              </span>
              <span>Danziger Str. 18 · 74889 Sinsheim</span>
            </div>
          </Reveal>
        </div>

        {submitted ? (
          <Reveal delay={0.1}>
            <div className="border border-[#FF3B00]/40 bg-[#FF3B00]/[0.05] p-8 lg:p-12 flex flex-col items-start" data-testid="contact-success">
              <span className="inline-flex h-14 w-14 items-center justify-center border border-[#FF3B00] mb-7">
                <CheckCircle2 size={28} className="text-[#FF3B00]" />
              </span>
              <h3 className="font-display text-3xl md:text-4xl text-white uppercase leading-tight mb-4">
                Danke! Ihre Anfrage<br />ist eingegangen.
              </h3>
              <p className="text-neutral-300 text-base leading-relaxed max-w-md mb-2">
                Wir melden uns <span className="text-white font-medium">innerhalb von 24 Stunden</span> persönlich
                bei Ihnen – ganz unverbindlich mit den nächsten Schritten.
              </p>
              <p className="text-neutral-500 text-sm mb-8">
                Sie möchten sofort loslegen? Schreiben Sie uns direkt per WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                {waHref && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:bg-[#1fbe5a] active:scale-95 transition-all duration-200"
                    data-testid="success-whatsapp"
                  >
                    <MessageCircle size={18} /> Direkt per WhatsApp
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:border-[#FF3B00] hover:text-[#FF3B00] transition-colors duration-300"
                  data-testid="success-new-request"
                >
                  Neue Anfrage senden
                </button>
              </div>
            </div>
          </Reveal>
        ) : (
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <input className={field} placeholder="Ihr Name *" value={form.name} onChange={set("name")} data-testid="input-name" />
              <input className={field} placeholder="Unternehmen" value={form.company} onChange={set("company")} data-testid="input-company" />
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <input className={field} type="email" placeholder="E-Mail *" value={form.email} onChange={set("email")} data-testid="input-email" />
              <input className={field} placeholder="Telefon (optional)" value={form.phone} onChange={set("phone")} data-testid="input-phone" />
            </div>

            <div>
              <p className="font-tech text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4">Gewünschtes Paket</p>
              <div className="flex flex-wrap gap-3">
                {PAKETE.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, paket: p }))}
                    className={`font-tech text-xs uppercase tracking-[0.1em] px-4 py-2.5 border transition-colors duration-200 ${
                      form.paket === p ? "border-[#FF3B00] bg-[#FF3B00] text-white" : "border-white/20 text-neutral-400 hover:border-white/50"
                    }`}
                    data-testid={`paket-${p.split(" ")[0].toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-tech text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4">Haben Sie bereits eine Website?</p>
              <div className="flex flex-wrap gap-3">
                {["Ja", "Nein"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, has_website: v }))}
                    className={`font-tech text-xs uppercase tracking-[0.1em] px-5 py-2.5 border transition-colors duration-200 ${
                      form.has_website === v ? "border-[#FF3B00] bg-[#FF3B00] text-white" : "border-white/20 text-neutral-400 hover:border-white/50"
                    }`}
                    data-testid={`has-website-${v.toLowerCase()}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              className={`${field} resize-none`}
              rows={4}
              placeholder="Erzählen Sie uns kurz von Ihrem Unternehmen *"
              value={form.message}
              onChange={set("message")}
              data-testid="input-message"
            />

            <button
              type="submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#FF3B00] text-white font-tech text-sm uppercase tracking-[0.14em] py-5 hover:bg-[#FF5C2B] active:scale-[0.99] transition-all duration-200 disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" /> Wird gesendet…</>
              ) : (
                <>Unverbindliche Anfrage senden <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></>
              )}
            </button>
          </form>
        </Reveal>
        )}
      </div>
    </section>
  );
}
